import React, { useEffect, useState, useContext } from "react";
import api from "../../../shared/api/axios";
import { useAuth } from "../../../shared/contexts/AuthContext";
import { setAccessToken } from '../../../shared/api/axios';
import { LanguageContext } from "../../../shared/contexts/LanguageContext";
import Modal from "../../components/Modal";
import { useTranslation } from 'react-i18next';

const inputStyle: React.CSSProperties = {
  width: "96%",
  padding: "10px",
  marginTop: "6px",
  borderRadius: "4px",
  border: "1px solid #ccc",
  fontSize: "16px",
};

const buttonStyle: React.CSSProperties = {
  padding: "10px 16px",
  backgroundColor: "#4CAF50",
  color: "#fff",
  border: "none",
  borderRadius: "4px",
  fontSize: "14px",
  cursor: "pointer",
};

const normalizeMe = (u: any) => ({
  profilename: u?.profilename ?? u?.displayName ?? u?.username ?? u?.name ?? "",
  email: u?.email ?? "",
});

const AccountSettingsMobile: React.FC = () => {
  const { user, setUser, setToken } = useAuth();
  const { t, i18n } = useTranslation();
  const langCtx = useContext(LanguageContext);
  const globalLanguage = langCtx?.language || i18n.language || "en";
  const [language, setLanguage] = useState(globalLanguage);
  const [profilename, setProfilename] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [original, setOriginal] = useState<{ profilename: string; email: string }>({
    profilename: "",
    email: "",
  });
  const dirty = profilename !== original.profilename;

  // Share via E-Mail (web)
const [shareOpen, setShareOpen] = useState(false);
const [shareTo, setShareTo] = useState('');
const [shareSending, setShareSending] = useState(false);


const [pwSuccess, setPwSuccess] = useState(false);

// Simple email validation
const isEmailAddr = (v: string) => /\S+@\S+\.\S+/.test(v);

// Build a web URL to your public/visitor profile page
const buildProfileUrl = () => {
  const myId = (user as any)?._id || (user as any)?.id || '';
  // Adjust the path if your route is different, e.g. `/visitor/${myId}`:
  return new URL(`/profile/${myId}`, window.location.origin).toString();
};

const handleShareEmail = async () => {
  if (!isEmailAddr(shareTo)) {
    alert(t('accountSettings.profileName.shareInvalidEmail'));
    return;
  }
  try {
    setShareSending(true);

    const link = buildProfileUrl();
    const subject = t('accountSettings.profileName.mailSubject', {
      name: profilename,
    });
    const body = t('accountSettings.profileName.mailBody', {
      name: profilename,
      link,
    });

    // Open default mail client with prefilled subject/body
    const mailto =
      `mailto:${encodeURIComponent(shareTo)}`
      + `?subject=${encodeURIComponent(subject)}`
      + `&body=${encodeURIComponent(body)}`;

    window.location.href = mailto;

    setShareOpen(false);
    setShareTo('');
  } finally {
    setShareSending(false);
  }
};

//Password Change
  const [currentPassword, setCurrentPassword] = useState("");
const [newPw, setNewPw] = useState("");
const [confirmPw, setConfirmPw] = useState("");
const [pwSaving, setPwSaving] = useState(false);

// --- PW flow modal state ---
const [pwFlowOpen, setPwFlowOpen] = useState(false);
const [pwStep, setPwStep] = useState<'current' | 'new'>('current');
const [pwError, setPwError] = useState<string>('');

// NEW: hidden/deactivation UI state
const [isDeactivated, setIsDeactivated] = useState(false);
const [deactivatedAt, setDeactivatedAt] = useState<string | null>(null);

const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
const [deactPw, setDeactPw] = useState('');
const [deactLoading, setDeactLoading] = useState(false);
const [reactivating, setReactivating] = useState(false);
const [deactError, setDeactError] = useState('');
const [showReactivateDialog, setShowReactivateDialog] = useState(false);
const [reactivateError, setReactivateError] = useState('');


//Password change
// open/close
const openPwFlow = () => {
  setPwError('');
  setPwSuccess(false);
  setCurrentPassword('');
  setNewPw('');
  setConfirmPw('');
  setPwStep('current');
  setPwSaving(false);
  setPwFlowOpen(true);
};
const closePwFlow = () => {
  if (pwSaving) return;
  setPwFlowOpen(false);
};

// Step 1 -> Step 2
const [pwChecking, setPwChecking] = useState(false);

const continueFromCurrent = async () => {
  setPwError('');

  if (!currentPassword) {
    setPwError(t('accountSettings.password.alerts.missingCurrentFields'));
    return;
  }

  try {
    setPwChecking(true);
    await api.post('/auth/check-current-password', { currentPassword });

    // ✅ only now continue to step 2
    setPwStep('new');
  } catch (err: any) {
    const backendMsg = err?.response?.data?.msg || '';

    // show under the field
    setPwError(
      backendMsg || t('accountSettings.password.alerts.currentIncorrect')
    );
  } finally {
    setPwChecking(false);
  }
};


// password policy: tweak as you like
const pwPolicy = (v: string) => ({
  len: v.length >= 8,
  digit: /\d/.test(v),
});
const isPwValid = (v: string) => {
  const p = pwPolicy(v);
  return p.len && p.digit;
};

// derived for step 2
const newPwOk = isPwValid(newPw);
const pwMatch = newPw === confirmPw;
const canSubmitPw = !!currentPassword && !!newPw && !!confirmPw && newPwOk && pwMatch && !pwSaving;

// Final submit (calls your existing endpoint)
const submitNewPassword = async () => {
  if (!canSubmitPw) return;
  try {
    setPwSaving(true);
    await api.post('/auth/change-password', { currentPassword, newPassword: newPw });


    setPwSuccess(true);
    setPwSaving(false);
    setCurrentPassword('');
    setNewPw('');
    setConfirmPw('');
  } catch (err: any) {
    setPwSaving(false);
    const backendMsg = err?.response?.data?.msg || '';

    if (/current password is incorrect/i.test(backendMsg)) {
      setPwStep('current');
      setPwError(t('accountSettings.password.alerts.currentIncorrect'));
    } else {
      setPwError(backendMsg || t('accountSettings.password.alerts.error'));
    }
  }
};


// Open dialog to ask for current password 
const openDeactivateDialog = () => {
  setDeactPw('');
  setDeactError('');
  setShowDeactivateDialog(true);
};

// Call backend to deactivate (requires current password)
const submitDeactivate = async () => {
  setDeactError('');

  if (!deactPw) {
    setDeactError(t('accountSettings.pauseProfile.alerts.missingPassword'));
    return;
  }

  try {
    setDeactLoading(true);

    const { data } = await api.post('/hidden/deactivate', { currentPassword: deactPw });
    setIsDeactivated(true);
    setDeactivatedAt(new Date().toISOString());
    setShowDeactivateDialog(false);

    // Optional: keep success alert if you want (or remove it if you prefer)
    // alert(data?.msg || t('accountSettings.pauseProfile.alerts.deactivatedSuccess'));
  } catch (e: any) {
    console.error(e);

    const backendMsg =
      e?.response?.data?.msg ||
      t('accountSettings.pauseProfile.alerts.deactivatedError');

    // ✅ show red text under input (no alert)
    setDeactError(String(backendMsg));
  } finally {
    setDeactLoading(false);
  }
};


// Reactivate with one click
const handleReactivate = async () => {
  try {
    setReactivateError('');
    setReactivating(true);

    await api.post('/hidden/activate', {});

    // ✅ update UI state
    setIsDeactivated(false);
    setDeactivatedAt(null);

    // ✅ close confirm modal
    setShowReactivateDialog(false);
  } catch (e: any) {
    const msg =
      e?.response?.data?.msg ||
      t('accountSettings.pauseProfile.reactivateConfirm.error');

    // ❌ no alert — show inline error
    setReactivateError(String(msg));
  } finally {
    setReactivating(false);
  }
};

const openReactivateDialog = () => {
  setShowReactivateDialog(true);
};

// --- validators ---
const isEmail = (v: string) => /\S+@\S+\.\S+/.test(v);


// --- Change Email state

// state
const [stage, setStage] = useState<'idle'|'codeSent'>('idle');
const [otp, setOtp] = useState('');
const [verifying, setVerifying] = useState(false);
const [resending, setResending] = useState(false);

// verify handler
const handleVerifyOTP = async () => {
  if (!otp || otp.length !== 6) {
    alert(t('accountSettings.emailChange.otpMissing'));
    return;
  }
  try {
    setVerifying(true);
    const { data } = await api.post('/auth/verify-email-change', { otp }, {
    });   

    // if backend returns new token, store it
    if (data?.accessToken) {
      setAccessToken(data.accessToken); // keeps axios interceptor happy
      setToken(data.accessToken);       // ✅ updates context state too
    }
    
    // refresh /users/me to show updated email
    const me = await api.get('/users/me',);
    const n = normalizeMe(me.data);
    setEmail(n.email);
    setUser({ ...(user || {}), ...me.data, ...n } as any);
    localStorage.setItem('unyProfile', JSON.stringify({ ...(user || {}), ...me.data, ...n }));

    setOtp('');
    setNewEmail('');              // optional: clear the “new email” box
    setStage('idle');
    setShowOtpDialog(false);
    alert(t('accountSettings.emailChange.otpSuccess'));
  } catch (e: any) {
    console.error(e);
    alert(e?.response?.data?.msg || t('accountSettings.emailChange.otpError'));
  } finally {
    setVerifying(false);
  }
};

// resend handler (optional)
const handleResendOTP = async () => {
  try {
    setResending(true);
    await api.post('/auth/resend-email-change-otp', {}, {
    });
    alert(t('accountSettings.emailChange.otpResendSuccess'));
  } catch (e: any) {
    console.error(e);
    alert(e?.response?.data?.msg || t('accountSettings.emailChange.otpResendError'));
  } finally {
    setResending(false);
  }
};

const [newEmail, setNewEmail] = useState("");
const [emailChanging, setEmailChanging] = useState(false);
const [showOtpDialog, setShowOtpDialog] = useState(false); // NEW

const emailIsSame = newEmail.trim().toLowerCase() === email.trim().toLowerCase();
const canOpenEmailDialog = isEmail(newEmail) && !emailIsSame && !emailChanging;

const handleEmailChangeRequest = async (): Promise<boolean> => {
  if (!newEmail) {
    alert(t('accountSettings.emailChange.missingNewEmail'));
    return false;
  }
  try {
    setEmailChanging(true);
    await api.post(
      "/auth/change-email-request",
      { newEmail },
    );
    setStage("codeSent");
    setShowOtpDialog(true);
    alert(t('accountSettings.emailChange.startSuccess'));
    return true;
  } catch (e: any) {
    console.error(e);
    alert(e?.response?.data?.msg || t("accountSettings.emailChange.startError"));
    return false;
  } finally {
    setEmailChanging(false);
  }
};

//Languages
const [originalLanguage, setOriginalLanguage] = useState("en");
const [savingLang, setSavingLang] = useState(false);
const languageDirty = language !== originalLanguage;


  // 1) Prefill from context if present; otherwise GET /users/me
  useEffect(() => {
  const init = async () => {
    try {
      if (user && (user as any)._id) {
        const n = normalizeMe(user);
        setProfilename(n.profilename);
        setEmail(n.email);
        setOriginal(n);

        const savedLang =
    (user as any)?.language ||
    localStorage.getItem("unyLanguage") ||
    i18n.language ||
    "en";

  setLanguage(savedLang);
  setOriginalLanguage(savedLang);

        // NEW: fetch deactivation status even when user is already in context
        try {
          const st = await api.get('/hidden/me/status', {
          });
          setIsDeactivated(!!st.data?.isDeactivated);
          setDeactivatedAt(st.data?.deactivatedAt || null);
        } catch (e) {
          console.warn('hidden status fetch failed', e);
        }

        return; // keep your early return
      }

      // Existing profile fetch
      const { data } = await api.get("/users/me", {
      });
      const n = normalizeMe(data);
      setProfilename(n.profilename);
      setEmail(n.email);
      setOriginal(n);
      setLanguage((data?.language as string) || "en");
      setOriginalLanguage((data?.language as string) || "en");

      // NEW: fetch deactivation status (initial load path)
      try {
        const st = await api.get('/hidden/me/status', {
        });
        setIsDeactivated(!!st.data?.isDeactivated);
        setDeactivatedAt(st.data?.deactivatedAt || null);
      } catch (e) {
        console.warn('hidden status fetch failed', e);
      }

    } catch (e) {
      console.error("Failed to load account settings", e);
    }
  };
  init();
}, [user]);

//Language UseEffect
useEffect(() => {
  setLanguage(globalLanguage);
  setOriginalLanguage(globalLanguage);
}, [globalLanguage]);


  // 2) Save (PUT /users/me) — same pattern as your Profile page
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dirty || saving) return;

    try {
      setSaving(true);
      const payload: any = {
        profilename: (profilename || "").trim(),
        // include common aliases to maximize backend compatibility
        name: (profilename || "").trim(),
        displayName: (profilename || "").trim(),
        username: (profilename || "").trim(),
      };

      await api.put("/users/me", payload, {
      });

      // Re-fetch to get the canonical values the server persisted
      const { data } = await api.get("/users/me", {
      });

      const n = normalizeMe(data);
      setProfilename(n.profilename);
      setEmail(n.email);
      setOriginal(n);

      // keep AuthContext + localStorage in sync
      const nextUser = { ...(user || {}), ...data, ...n };
      setUser(nextUser as any);
      localStorage.setItem("unyProfile", JSON.stringify(nextUser));

      alert(t('accountSettings.pageAlerts.saveSuccess'));
    } catch (err) {
      console.error(err);
      alert(t('accountSettings.pageAlerts.saveError'));
    } finally {
      setSaving(false);
    }
  };

  //Language
  const handleSaveLanguage = async (nextLang: string) => {
    if (savingLang) return;
  
    try {
      setSavingLang(true);
  
      // ✅ instantly update UI + persist locally (so refresh keeps it)
      setLanguage(nextLang);
      setOriginalLanguage(nextLang);
      langCtx?.changeLanguage(nextLang);
  
      // 🔧 optional: persist to backend (keep if you want multi-device sync)
      await api.put('/users/me', { language: nextLang });
       const { data } = await api.get('/users/me');
        setUser({ ...(user || {}), ...data, language: nextLang } as any);
        localStorage.setItem('unyProfile', JSON.stringify({ ...(user || {}), ...data, language: nextLang }));

      
  
      // optional toast
      // alert(t('accountSettings.language.alerts.success'));
    } catch (e) {
      console.error(e);
      alert(t('accountSettings.language.alerts.error'));
    } finally {
      setSavingLang(false);
    }
  };
  
  
  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#02040b" }}>
    
      {/* Main content area */}
      <main
  style={{
    padding: 24,
    boxSizing: "border-box",
    color: "#e5e7eb",
  }}
>
        <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>
          {t('accountSettings.page.title')}
        </h2>
        <p style={{ color: "#9ca3af", fontSize: 13, marginBottom: 18 }}>
        {t('accountSettings.page.subtitle')}
        </p>

        <form
  onSubmit={handleSave}
  autoComplete="off"
  style={{ width: "100%" }}  // or just remove the style entirely
>          {/* Profile Name + share */}
          <section
            style={{
              borderRadius: 12,
              border: "1px solid rgba(148,163,184,0.4)",
              background: "rgba(15,23,42,0.9)",
              padding: 16,
              marginBottom: 16,
            }}
          >
            <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>
            {t('accountSettings.profileName.sectionTitle')}
            </h3>
            <p style={{ color: "#9ca3af", fontSize: 13, marginBottom: 8 }}>
            {t('accountSettings.profileName.sectionDescription')}
            </p>

            <label style={{ fontSize: 13, color: "#9ca3af", display: "block" }}>
            {t('accountSettings.profileName.label')}
            </label>
            <input
              type="text"
              value={profilename}
              readOnly
              disabled
              style={{
                ...inputStyle,
                marginTop: 6,
                background: "#020617",
                border: "1px solid rgba(148,163,184,0.6)",
                color: "#e5e7eb",
                cursor: "not-allowed",
              }}
            />

            <div style={{ marginTop: 10 }}>
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                style={{
                  ...buttonStyle,
                  backgroundColor: "#2d6cdf",
                  borderRadius: 999,
                  fontSize: 13,
                }}
              >
                {t('accountSettings.profileName.shareButton')}
              </button>
            </div>
          </section>

          {/* Change Email */}
          <section
            style={{
              borderRadius: 12,
              border: "1px solid rgba(148,163,184,0.4)",
              background: "rgba(15,23,42,0.9)",
              padding: 16,
              marginBottom: 16,
            }}
          >
            <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>
            {t('accountSettings.emailChange.sectionTitle')}
            </h3>
            <p style={{ color: "#9ca3af", fontSize: 13, marginBottom: 12 }}>
            {t('accountSettings.emailChange.sectionDescription')}
            </p>

            {stage === "idle" && (
              <>
                <div style={{ marginBottom: 12 }}>
                  <label
                    style={{
                      fontSize: 13,
                      color: "#9ca3af",
                      display: "block",
                      marginBottom: 4,
                    }}
                  >
            {t('accountSettings.emailChange.currentEmail')}
            </label>
                  <input
                    type="email"
                    value={email}
                    readOnly
                    disabled
                    style={{
                      ...inputStyle,
                      background: "#020617",
                      border: "1px solid rgba(148,163,184,0.6)",
                      color: "#e5e7eb",
                      opacity: 0.8,
                      cursor: "not-allowed",
                    }}
                  />
                </div>

                <div style={{ marginBottom: 8 }}>
                  <label
                    style={{
                      fontSize: 13,
                      color: "#9ca3af",
                      display: "block",
                      marginBottom: 4,
                    }}
                  >
                    {t('accountSettings.emailChange.newEmail')}
                  </label>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    style={{
                      ...inputStyle,
                      background: "#020617",
                      border: "1px solid rgba(148,163,184,0.6)",
                      color: "#e5e7eb",
                    }}
                  />
                </div>

                {newEmail && !isEmail(newEmail) && (
                  <small style={{ color: "#f97316", fontSize: 12 }}>
                    {t('accountSettings.emailChange.validationInvalid')}
                  </small>
                )}
                {emailIsSame && (
                  <small style={{ color: "#f97316", fontSize: 12, display: "block" }}>
                    {t('accountSettings.emailChange.validationSame')}
                  </small>
                )}

                <button
                  type="button"
                  disabled={!canOpenEmailDialog}
                  onClick={handleEmailChangeRequest}
                  style={{
                    ...buttonStyle,
                    marginTop: 10,
                    backgroundColor: "#16a34a",
                    borderRadius: 999,
                    fontSize: 13,
                    opacity: canOpenEmailDialog ? 1 : 0.4,
                    cursor: canOpenEmailDialog ? "pointer" : "not-allowed",
                  }}
                >
                  {emailChanging
                    ? t('accountSettings.emailChange.startButtonLoading')
                    : t('accountSettings.emailChange.startButton')}
                </button>
              </>
            )}
          </section>

          {/* Change Password (button -> modal) */}
          <section
            style={{
              borderRadius: 12,
              border: "1px solid rgba(148,163,184,0.4)",
              background: "rgba(15,23,42,0.9)",
              padding: 16,
              marginBottom: 16,
            }}
          >
            <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>
            {t('accountSettings.password.sectionTitle')}
            </h3>
            <p style={{ color: "#9ca3af", fontSize: 13, marginBottom: 8 }}>
            {t('accountSettings.password.sectionDescription')}
            </p>

            <button
              type="button"
              onClick={openPwFlow}
              style={{
                ...buttonStyle,
                backgroundColor: "#22c55e",
                borderRadius: 999,
                fontSize: 13,
              }}
            >
            {t('accountSettings.password.sectionButton')}
            </button>
          </section>

          {/* Language */}
<section
  style={{
    borderRadius: 12,
    border: "1px solid rgba(148,163,184,0.4)",
    background: "rgba(15,23,42,0.9)",
    padding: 16,
    marginBottom: 16,
  }}
>
  <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>
    {t('accountSettings.language.sectionTitle')}
  </h3>
  <p style={{ color: "#9ca3af", fontSize: 13, marginBottom: 12 }}>
    {t('accountSettings.language.sectionDescription')}
  </p>

  <div
    style={{
      display: "flex",
      gap: 8,
      alignItems: "flex-end",
      marginBottom: 4,
    }}
  >
    <div style={{ flex: 1 }}>
      <select
        value={language}
        onChange={(e) => handleSaveLanguage(e.target.value)}
        style={{
          ...inputStyle,
          background: "#020617",
          border: "1px solid rgba(148,163,184,0.6)",
          color: "#e5e7eb",
        }}
      >
        <option value="en">{t('accountSettings.language.options.en')}</option>
        <option value="de">{t('accountSettings.language.options.de')}</option>
        <option value="fr">{t('accountSettings.language.options.fr')}</option>
        <option value="es">{t('accountSettings.language.options.es')}</option>
        <option value="pt">{t('accountSettings.language.options.pt')}</option>
        <option value="nl">{t('accountSettings.language.options.nl')}</option>
        <option value="it">{t('accountSettings.language.options.it')}</option>
        <option value="pl">{t('accountSettings.language.options.pl')}</option>

        <option value="hu">{t('accountSettings.language.options.hu')}</option>
        <option value="cs">{t('accountSettings.language.options.cs')}</option>
        <option value="ru">{t('accountSettings.language.options.ru')}</option>
        <option value="uk">{t('accountSettings.language.options.uk')}</option>

        <option value="ja">{t('accountSettings.language.options.ja')}</option>
        <option value="ko">{t('accountSettings.language.options.ko')}</option>
        <option value="ar">{t('accountSettings.language.options.ar')}</option>
        <option value="vi">{t('accountSettings.language.options.vi')}</option>

        {/* Optional extras */}
        <option value="tr">{t('accountSettings.language.options.tr')}</option>
        <option value="zh">{t('accountSettings.language.options.zh')}</option>
      </select>
    </div>
  </div>
</section>


          {/* Pause profile */}
          <section
            style={{
              borderRadius: 12,
              border: "1px solid rgba(148,163,184,0.4)",
              background: "rgba(15,23,42,0.9)",
              padding: 16,
              marginBottom: 32,
            }}
          >
            <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>
            {t('accountSettings.language.pauseprofile')}
            </h3>
            <p style={{ color: "#9ca3af", fontSize: 13, marginBottom: 8 }}>
            {t('accountSettings.language.pausedescr')}
            </p>

            {isDeactivated ? (
  <p style={{ color: "#f97316", marginTop: 0, fontSize: 13 }}>
    {t("accountSettings.pauseProfile.statusDeactivated")}
    {deactivatedAt
      ? t("accountSettings.pauseProfile.since", {
          date: new Date(deactivatedAt).toLocaleString(),
        })
      : ""}
    
  </p>
) : (
  <p style={{ color: "#22c55e", marginTop: 0, fontSize: 13 }}>
    {t("accountSettings.pauseProfile.statusActive")}
  </p>
)}


            {!isDeactivated ? (
              <button
                type="button"
                onClick={openDeactivateDialog}
                style={{
                  ...buttonStyle,
                  backgroundColor: "#f97316",
                  borderRadius: 999,
                  fontSize: 13,
                  marginTop: 8,
                }}
              >
                {t("accountSettings.pauseProfile.deactivateButton")}
              </button>
            ) : (
              <button
                type="button"
                onClick={openReactivateDialog}
                disabled={reactivating}
                style={{
                  ...buttonStyle,
                  backgroundColor: "#2d6cdf",
                  borderRadius: 999,
                  fontSize: 13,
                  marginTop: 8,
                  opacity: reactivating ? 0.6 : 1,
                }}
              >
                {reactivating ? t("accountSettings.pauseProfile.reactivating") : t("accountSettings.pauseProfile.reactivateButton")}

              </button>
            )}
          </section>
        </form>

        {/* Deactivate dialog */}
        <Modal
          open={showDeactivateDialog}
          onClose={() => {
            if (!deactLoading) setShowDeactivateDialog(false);
          }}
          title={t('accountSettings.pauseProfile.deactivateModalTitle')}

        >
          <>
            <p>
            <p>{t('accountSettings.pauseProfile.deactivateModalDescription')}</p>

            </p>

            <label style={{ display: "block", width: "100%", marginBottom: 6 }}>
  {t('accountSettings.pauseProfile.deactivateModalLabel')}
</label>
<input
  type="password"
  value={deactPw}
  onChange={(e) => {
    setDeactPw(e.target.value);
    setDeactError('');
  }}
  onKeyDown={(e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (!deactLoading && deactPw) submitDeactivate();
    }
  }}
  style={{ ...inputStyle, marginBottom: 8 }}
  autoComplete="current-password"
/>


{!!deactError && (
  <div style={{ color: "#c0392b", marginBottom: 10 }}>
    {deactError}
  </div>
)}    


            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => setShowDeactivateDialog(false)}
                disabled={deactLoading}
                style={{ ...buttonStyle, backgroundColor: "#999" }}
              >
                {t('accountSettings.pauseProfile.modalCancel')}
              </button>
              <button
                type="button"
                onClick={submitDeactivate}
                disabled={deactLoading || !deactPw}
                style={{
                  ...buttonStyle,
                  backgroundColor: "#f97316",
                  opacity: deactLoading || !deactPw ? 0.6 : 1,
                }}
              >
                {deactLoading
                  ? t('accountSettings.pauseProfile.modalDeactivating')
                  : t('accountSettings.pauseProfile.modalDeactivate')}
              </button>
            </div>
          </>
        </Modal>

  <Modal
  open={showReactivateDialog}
  onClose={reactivating ? () => {} : () => setShowReactivateDialog(false)}
  title={t('accountSettings.pauseProfile.reactivateConfirm.title')}
  closeOnOverlayClick={!reactivating}
  footer={
    <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
      <button
        type="button"
        onClick={() => setShowReactivateDialog(false)}
        disabled={reactivating}
        style={{ ...buttonStyle, backgroundColor: "#999" }}
      >
        {t('common.no')}
      </button>

      <button
        type="button"
        onClick={handleReactivate}
        disabled={reactivating}
        style={{
          ...buttonStyle,
          backgroundColor: "#22c55e",
          opacity: reactivating ? 0.7 : 1,
          cursor: reactivating ? "not-allowed" : "pointer",
        }}
      >
        {reactivating
          ? t('accountSettings.pauseProfile.reactivateConfirm.yesBusy')
          : t('common.yes')}
      </button>
    </div>
  }
>
  <p style={{ margin: 0 }}>
    {t('accountSettings.pauseProfile.reactivateConfirm.body')}
  </p>

  {!!reactivateError && (
    <div style={{ color: "#c0392b", marginTop: 10 }}>
      {reactivateError}
    </div>
  )}
</Modal>



        {/* Share profile modal */}
        <Modal
          open={shareOpen}
          onClose={() => {
            if (!shareSending) setShareOpen(false);
          }}
          title={t('accountSettings.profileName.shareModalTitle')}
        >
          <>
            <p>
            {t('accountSettings.profileName.shareModalDescription')}
            </p>

            <label>{t('accountSettings.profileName.shareRecipientLabel')}</label>
            <input
  type="email"
  value={shareTo}
  onChange={(e) => setShareTo(e.target.value)}
  onKeyDown={(e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (!shareSending && isEmailAddr(shareTo)) handleShareEmail();
    }
  }}
  style={{ ...inputStyle, marginBottom: 12 }}
  placeholder={t('accountSettings.profileName.shareRecipientPlaceholder')}
  autoComplete="email"
/>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => setShareOpen(false)}
                disabled={shareSending}
                style={{ ...buttonStyle, backgroundColor: "#999" }}
              >
                {t('accountSettings.profileName.shareCancel')}
              </button>
              <button
                type="button"
                onClick={handleShareEmail}
                disabled={shareSending || !isEmailAddr(shareTo)}
                style={{
                  ...buttonStyle,
                  backgroundColor: "#2d6cdf",
                  opacity: shareSending || !isEmailAddr(shareTo) ? 0.6 : 1,
                }}
              >
                {shareSending
                  ? t('accountSettings.profileName.shareSubmitting')
                  : t('accountSettings.profileName.shareSubmit')}
              </button>
            </div>
          </>
        </Modal>

        {/* Email OTP modal */}
        <Modal
          open={showOtpDialog}
          onClose={() => {
            if (!verifying && !resending) {
              setShowOtpDialog(false);
              setStage("idle");
              setOtp("");
            }
          }}
          title={t('accountSettings.emailChange.otpModalTitle')}
          width={360}
        >
          <>
          <p>
            {t('accountSettings.emailChange.otpModalDescription', {
             email: newEmail || t('accountSettings.emailChange.fallbackEmail', 'your new email'),
            })}
          </p>


            <label htmlFor="otp">{t('accountSettings.emailChange.otpLabel')}</label>
            <input
              id="otp"
              inputMode="numeric"
              pattern="\d*"
              maxLength={6}
              value={otp}
              onChange={(e) =>
                setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              style={{ ...inputStyle, marginBottom: 12 }}
              placeholder="••••••"
              autoFocus
            />

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => {
                  setShowOtpDialog(false);
                  setStage("idle");
                  setOtp("");
                }}
                style={{ ...buttonStyle, backgroundColor: "#999" }}
                disabled={verifying || resending}
              >
                {t('accountSettings.emailChange.otpCancel')}
              </button>
              <button
                type="button"
                onClick={handleResendOTP}
                style={{
                  ...buttonStyle,
                  backgroundColor: "#555",
                  opacity: resending ? 0.6 : 1,
                }}
                disabled={resending}
              >
                {resending
                  ? t('accountSettings.emailChange.otpResending')
                  : t('accountSettings.emailChange.otpResend')}
              </button>
              <button
                type="button"
                onClick={handleVerifyOTP}
                style={{
                  ...buttonStyle,
                  backgroundColor: "#2d6cdf",
                  opacity: verifying || otp.length !== 6 ? 0.6 : 1,
                }}
                disabled={verifying || otp.length !== 6}
              >
                {verifying
                  ? t('accountSettings.emailChange.otpVerifying')
                  : t('accountSettings.emailChange.otpVerify')}
              </button>
            </div>
          </>
        </Modal>

        {/* Password flow modal */}
        <Modal
  open={pwFlowOpen}
  onClose={() => {
    if (!pwSaving) closePwFlow();
  }}
  title={
    pwSuccess
      ? t("accountSettings.passwordFlow.successTitle")
      : pwStep === "current"
        ? t("accountSettings.passwordFlow.titleConfirm")
        : t("accountSettings.passwordFlow.titleChooseNew")
  }
  
  closeOnOverlayClick={pwStep === "current"}
>
  {pwStep === "current" ? (
    <>
      <p>{t("accountSettings.passwordFlow.enterCurrentHint")}</p>

      <label>{t("accountSettings.passwordFlow.currentLabel")}</label>
      <input
  type="password"
  value={currentPassword}
  onChange={(e) => {
    setCurrentPassword(e.target.value);
    setPwError("");
  }}
  onKeyDown={(e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (!pwSaving && !pwChecking && currentPassword) continueFromCurrent();
    }
  }}
  style={{ ...inputStyle, marginBottom: 12 }}
  autoComplete="current-password"
  placeholder={t("accountSettings.passwordFlow.currentPlaceholder")}
/>


      {!!pwError && (
        <div style={{ color: "#c0392b", marginBottom: 10 }}>{pwError}</div>
      )}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button
          type="button"
          onClick={closePwFlow}
          disabled={pwSaving}
          style={{ ...buttonStyle, backgroundColor: "#999" }}
        >
          {t("common.cancel")}
        </button>
        <button
  type="button"
  onClick={continueFromCurrent}
  disabled={pwSaving || pwChecking || !currentPassword}
  style={{
    ...buttonStyle,
    backgroundColor: "#2d6cdf",
    opacity: pwSaving || pwChecking || !currentPassword ? 0.6 : 1,
  }}
>
  {pwChecking ? t("common.checking") : t("common.continue")}
</button>
      </div>
    </>
 ) : pwSuccess ? (
  // ✅ SUCCESS SCREEN (after password changed)
  <div style={{ textAlign: "center", padding: "8px 0" }}>
    <div style={{ fontSize: 42, marginBottom: 8 }}>✅</div>

    

    <p style={{ color: "#9ca3af", marginTop: 8 }}>
      {t("accountSettings.passwordFlow.successBody")}
    </p>

    <div style={{ display: "flex", justifyContent: "center", marginTop: 14 }}>
      <button
        type="button"
        onClick={() => {
          setPwFlowOpen(false);
          setPwSuccess(false);
          setPwStep("current");
          setPwError("");
          setCurrentPassword("");
          setNewPw("");
          setConfirmPw("");
        }}
        style={{
          ...buttonStyle,
          backgroundColor: "#22c55e",
          borderRadius: 999,
        }}
      >
        {t("accountSettings.passwordFlow.done")}
      </button>
    </div>
  </div>
) : (
  // ✅ NORMAL "NEW PASSWORD" UI (before success)
  <>
  <p>{t("accountSettings.passwordFlow.policyHint")}</p>

<label style={{ display: "block", width: "100%", marginBottom: 6 }}>
  {t("accountSettings.passwordFlow.newLabel")}
</label>
<input
  type="password"
  value={newPw}
  onChange={(e) => {
    setNewPw(e.target.value);
    setPwError("");
  }}
  style={{ ...inputStyle, marginBottom: 12 }}
  placeholder={t("accountSettings.passwordFlow.newPlaceholder")}
  autoComplete="new-password"
/>

<label style={{ display: "block", width: "100%", marginTop: 10, marginBottom: 6 }}>
  {t("accountSettings.passwordFlow.confirmLabel")}
</label>
<input
  type="password"
  value={confirmPw}
  onChange={(e) => {
    setConfirmPw(e.target.value);
    setPwError("");
  }}
  style={{ ...inputStyle, marginBottom: 12 }}
  placeholder={t("accountSettings.passwordFlow.confirmPlaceholder")}
  autoComplete="new-password"
/>


    {/* keep your chips block here */}

    {!!pwError && (
      <div style={{ color: "#c0392b", marginBottom: 10 }}>{pwError}</div>
    )}

    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
      <button
        type="button"
        onClick={closePwFlow}
        disabled={pwSaving}
        style={{ ...buttonStyle, backgroundColor: "#999" }}
      >
        {t("common.cancel")}
      </button>

      <button
        type="button"
        onClick={submitNewPassword}
        disabled={!canSubmitPw}
        style={{
          ...buttonStyle,
          backgroundColor: "#22c55e",
          opacity: canSubmitPw ? 1 : 0.5,
          cursor: canSubmitPw ? "pointer" : "not-allowed",
        }}
      >
        {pwSaving
          ? t("accountSettings.passwordFlow.updating")
          : t("accountSettings.passwordFlow.updatePassword")}
      </button>
    </div>
  </>
)}

</Modal>

      </main>
    </div>
  );

  };
  
  export default AccountSettingsMobile;
  